/**
 * components/StatusChip.tsx
 * Displays project status as a chip badge.
 */

import React from 'react';
import { Chip, ChipProps } from '@mui/material';
import { ProjectStatus } from '../types/dashboard.types';

interface StatusChipProps extends Omit<ChipProps, 'label'> {
  status: ProjectStatus;
  size?: 'small' | 'medium';
}

export const StatusChip = React.forwardRef<HTMLDivElement, StatusChipProps>(
  ({ status, size = 'small', ...props }, ref) => {
    const getStatusConfig = (status: ProjectStatus) => {
      switch (status) {
        case 'draft':
          return {
            label: 'Draft',
            backgroundColor: 'var(--fx-surface-muted, #F1F5F9)',
            color: 'var(--fx-surface-textsecondary, #64748B)',
          };
        case 'publish':
          return {
            label: 'Published',
            backgroundColor: 'var(--fx-colors-success-100, #D1FAE5)',
            color: '#059669',
          };
        default:
          return {
            label: status,
            backgroundColor: 'var(--fx-surface-muted, #F1F5F9)',
            color: 'var(--fx-surface-textsecondary, #64748B)',
          };
      }
    };

    const config = getStatusConfig(status);

    return (
      <Chip
        ref={ref}
        label={config.label}
        size={size}
        sx={{
          backgroundColor: config.backgroundColor,
          color: config.color,
          fontWeight: 500,
          fontSize: size === 'small' ? '0.75rem' : '0.875rem',
        }}
        {...props}
      />
    );
  }
);

StatusChip.displayName = 'StatusChip';
